import { useNavigate, useParams } from '@tanstack/react-router'
import type { DealFlowMatchData } from '@workspace/api-backend'
import { ConnectDialog } from '@workspace/seedforge-features/profile'
import { formatAmount, raiseStageLabel, stageLabel } from '@workspace/seedforge-shared'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@workspace/ui/components/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FullScreenState } from '@workspace/ui/components/full-screen-state'
import { cn, getInitials } from '@workspace/ui/lib/utils'
import {
  Archive,
  ArrowRight,
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileSearch,
  Mail,
  ScanSearch,
  Settings,
  Sparkles,
  ThumbsDown,
} from 'lucide-react'
import { useState } from 'react'
import { InvestorLayout } from '../../components/investor-layout'
import { CustomDiligenceDialog } from '../../features/deal-flow/components/custom-diligence-dialog'
import { PassDialog } from '../../features/deal-flow/components/pass-dialog'
import {
  useConnectDealFlowMatchMutation,
  useDealFlowMatchesQuery,
  useDealFlowSubscriptionStatusQuery,
  usePassDealFlowMatchMutation,
  useSubscribeToDealFlowMutation,
  useUnsubscribeFromDealFlowMutation,
} from '../../hooks/api'

// ── Components ───────────────────────────────────────────────────────────

const STRONG_FIT_THRESHOLD = 75

type ScoreTier = 'strong' | 'neutral'

function tierFor(score: number | null): ScoreTier {
  if (score === null) return 'neutral'
  return score >= STRONG_FIT_THRESHOLD ? 'strong' : 'neutral'
}

function tierClasses(tier: ScoreTier) {
  if (tier === 'strong') {
    return {
      border: 'border-emerald-400/30 bg-emerald-400/5 hover:brightness-110',
      scoreText: 'text-emerald-300',
    }
  }
  return {
    border: 'border-border/60 bg-card/30 hover:border-border hover:bg-card/50',
    scoreText: 'text-foreground',
  }
}

function StartupTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded border border-border/50 bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground">
      {children}
    </span>
  )
}

function ScoreColumn({ label, score }: { label: string; score: number }) {
  return (
    <div className="flex min-w-0 flex-col items-end leading-tight">
      <span className="text-3xl font-bold tabular-nums text-primary">{score}</span>
      <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-widest text-primary/80">
        {label}
      </span>
    </div>
  )
}

function DiligenceButton({
  customEvaluation,
  investorId,
  onStart,
}: {
  customEvaluation: DealFlowMatchData['customEvaluation']
  investorId: string
  onStart: () => void
}) {
  const navigate = useNavigate()
  const status = customEvaluation?.status

  if (status === 'COMPLETED' && customEvaluation?.evaluationId) {
    return (
      <SecondaryButton
        size="sm"
        icon={<FileSearch />}
        onClick={(e) => {
          e.stopPropagation()
          navigate({
            to: '/app/investor/$id/evaluation/$evaluationId',
            params: { id: investorId, evaluationId: customEvaluation.evaluationId as string },
          })
        }}
      >
        View diligence
      </SecondaryButton>
    )
  }

  if (status === 'PENDING_PERSONA' || status === 'PENDING_EVALUATION') {
    return (
      <SecondaryButton size="sm" loading onClick={(e) => e.stopPropagation()}>
        Diligence running…
      </SecondaryButton>
    )
  }

  return (
    <CtaButton
      size="sm"
      icon={<ScanSearch />}
      onClick={(e) => {
        e.stopPropagation()
        onStart()
      }}
    >
      Get custom diligence
    </CtaButton>
  )
}

function StartupCard({
  startup,
  investorId,
  onPass,
  onConnect,
  onCustomDiligence,
  onViewProfile,
}: {
  startup: DealFlowMatchData
  investorId: string
  onPass: () => void
  onConnect: () => void
  onCustomDiligence: () => void
  onViewProfile: () => void
}) {
  const profile = startup.profile.startup
  const customScore = startup.customEvaluation?.score ?? null
  const tier = tierFor(customScore)
  const t = tierClasses(tier)

  const metaParts = [
    profile.categoryName,
    stageLabel(profile.productStage) || null,
    profile.location,
    profile.fundraisingStage &&
      [raiseStageLabel(profile.fundraisingStage), formatAmount(profile.fundraisingAmount) || null]
        .filter(Boolean)
        .join(' '),
  ].filter(Boolean) as string[]

  return (
    <button
      type="button"
      onClick={onViewProfile}
      aria-label={`View ${profile.name} profile`}
      className={cn(
        'block w-full cursor-pointer rounded-xl border px-5 py-4 text-left transition-colors',
        t.border
      )}
    >
      <div className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border/60 bg-card text-xs font-semibold text-muted-foreground">
            {getInitials(profile.name)}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-foreground">{profile.name}</h3>
            {metaParts.length > 0 && (
              <p className="truncate text-xs text-muted-foreground">
                {metaParts.map((part, index) => (
                  <span key={part}>
                    {index > 0 && <span className="text-muted-foreground/50"> · </span>}
                    {part}
                  </span>
                ))}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-4">
            {customScore !== null && <ScoreColumn label="Custom" score={customScore} />}
          </div>
        </div>

        {/* Description */}
        {profile.description && (
          <p className="text-sm leading-relaxed text-foreground/85">{profile.description}</p>
        )}

        {/* Reasoning */}
        {startup.reasoning && (
          <div className="rounded-lg border border-border/40 bg-muted/20 px-3.5 py-2.5">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
              <Sparkles className="size-3" />
              Why this startup
            </p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {startup.reasoning}
            </p>
          </div>
        )}

        {/* Tags */}
        {profile.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {profile.tags.map((tag) => (
              <StartupTag key={tag}>{tag}</StartupTag>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 border-t border-border/40 pt-3">
          <SecondaryButton size="sm">
            View profile
            <ChevronRight className="size-3.5" />
          </SecondaryButton>
          <DiligenceButton
            customEvaluation={startup.customEvaluation}
            investorId={investorId}
            onStart={onCustomDiligence}
          />
          <CtaButton
            size="sm"
            icon={<ArrowRight />}
            onClick={(e) => {
              e.stopPropagation()
              onConnect()
            }}
            className="ml-auto"
          >
            Connect
          </CtaButton>
          <SecondaryButton
            tone="danger"
            size="sm"
            icon={<ThumbsDown />}
            onClick={(e) => {
              e.stopPropagation()
              onPass()
            }}
          >
            Pass
          </SecondaryButton>
        </div>
      </div>
    </button>
  )
}

// ── Subscribe gate ───────────────────────────────────────────────────────

function SubscribeGate({
  onSubscribe,
  isSubscribing,
}: {
  onSubscribe: () => void
  isSubscribing: boolean
}) {
  return (
    <div className="flex min-h-[calc(100vh-6rem)] flex-col items-center justify-center text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-emerald-400/10">
        <Bell className="size-7 text-emerald-400" />
      </div>
      <h2 className="mt-5 text-xl font-semibold text-foreground">Subscribe to Deal Flow</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Get notified about startups that match your investment thesis. We'll only email you about
        startups our AI scores as fundable based on your criteria. No noise, no irrelevant pitches.
      </p>

      <ul className="mt-6 space-y-2 text-left">
        <li className="flex items-start gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-400" />
          <span>Curated matches based on your thesis</span>
        </li>
        <li className="flex items-start gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-400" />
          <span>Email alerts only for fundable startups</span>
        </li>
        <li className="flex items-start gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-400" />
          <span>Pass or connect with one click</span>
        </li>
      </ul>

      <CtaButton onClick={onSubscribe} loading={isSubscribing} className="mt-8" icon={<Mail />}>
        {isSubscribing ? 'Subscribing...' : 'Subscribe to Deal Flow'}
      </CtaButton>
      <p className="mt-3 text-xs text-muted-foreground/70">
        Unsubscribe anytime from your preferences.
      </p>
    </div>
  )
}

// ── Section ──────────────────────────────────────────────────────────────

function DealFlowSection({
  title,
  subtitle,
  count,
  startups,
  renderCard,
}: {
  title: string
  subtitle: string
  count: number
  startups: DealFlowMatchData[]
  renderCard: (startup: DealFlowMatchData) => React.ReactNode
}) {
  if (count === 0) return null
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-2">
        <div className="min-w-0">
          <h2 className="flex items-baseline gap-2 text-xs font-semibold uppercase tracking-widest text-foreground">
            {title}
            <span className="rounded-full border border-border/60 bg-card/40 px-1.5 py-0 text-[10px] font-medium tabular-nums text-muted-foreground">
              {count}
            </span>
          </h2>
          <p className="mt-1 truncate text-xs text-muted-foreground/70">{subtitle}</p>
        </div>
      </div>
      <div className="mt-4 space-y-4">{startups.map(renderCard)}</div>
    </section>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────

type DialogKind = 'connect' | 'pass' | 'diligence'

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pre-screen', label: 'Pre-screen' },
  { key: 'ai-session', label: 'AI Session' },
] as const
type FilterKey = (typeof FILTERS)[number]['key']

const SORTS = [
  { key: 'relevant', label: 'Most relevant' },
  { key: 'best-score', label: 'Best diligence score' },
  { key: 'recent', label: 'Most recent' },
] as const
type SortKey = (typeof SORTS)[number]['key']

// The investor's own custom diligence score; SeedForge's council score never
// reaches investors.
function customScore(match: DealFlowMatchData): number {
  return match.customEvaluation?.score ?? -1
}

function sortStartups(startups: DealFlowMatchData[], sort: SortKey): DealFlowMatchData[] {
  if (sort === 'relevant') return startups
  const copy = [...startups]
  if (sort === 'best-score') {
    return copy.sort((a, b) => customScore(b) - customScore(a))
  }
  return copy.sort((a, b) => (b.firstSeenAt ?? '').localeCompare(a.firstSeenAt ?? ''))
}

export function DealFlowPage() {
  const { id } = useParams({ from: '/app/investor/$id/deal-flow' })
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<FilterKey>('all')
  const [sort, setSort] = useState<SortKey>('relevant')
  const [activeDialog, setActiveDialog] = useState<{
    kind: DialogKind
    startup: DealFlowMatchData
  } | null>(null)

  const subscriptionQuery = useDealFlowSubscriptionStatusQuery(id)
  const subscribeMutation = useSubscribeToDealFlowMutation(id)
  const unsubscribeMutation = useUnsubscribeFromDealFlowMutation(id)
  const matchesQuery = useDealFlowMatchesQuery(id)
  const passMutation = usePassDealFlowMatchMutation(id)
  const connectMutation = useConnectDealFlowMatchMutation(id)

  const matches = matchesQuery.data?.data ?? []
  const visibleStartups = matches.filter((s) => !dismissed.has(s.profile.startupId))
  // Filter is by AI Session presence (council pitch session), matching the
  // term used in the email digest. Sections still split by custom diligence
  // status within whatever the filter let through.
  const filtered = visibleStartups.filter((s) => {
    if (filter === 'ai-session') return s.profile.pitchSessionId !== null
    if (filter === 'pre-screen') return s.profile.pitchSessionId === null
    return true
  })
  // Recombee order is the array order — preserve it within each bucket.
  const evaluated = sortStartups(
    filtered.filter((s) => s.customEvaluation?.status === 'COMPLETED'),
    sort
  )
  const inProgress = sortStartups(
    filtered.filter(
      (s) =>
        s.customEvaluation?.status === 'PENDING_PERSONA' ||
        s.customEvaluation?.status === 'PENDING_EVALUATION'
    ),
    sort
  )
  const fresh = sortStartups(
    filtered.filter((s) => !s.customEvaluation || s.customEvaluation.status === 'FAILED'),
    sort
  )
  const filteredCount = evaluated.length + inProgress.length + fresh.length
  const sortLabel = SORTS.find((s) => s.key === sort)?.label ?? 'Most relevant'

  const renderCard = (startup: DealFlowMatchData) => (
    <StartupCard
      key={startup.profile.startupId}
      startup={startup}
      investorId={id}
      onPass={() => setActiveDialog({ kind: 'pass', startup })}
      onConnect={() => setActiveDialog({ kind: 'connect', startup })}
      onCustomDiligence={() => setActiveDialog({ kind: 'diligence', startup })}
      onViewProfile={() =>
        navigate({
          to: '/app/investor/$id/deal-flow/$startupId',
          params: { id, startupId: startup.profile.startupId },
        })
      }
    />
  )

  const closeDialog = () => setActiveDialog(null)
  const handleConfirmPass = (reasons: string[], note: string) => {
    if (!activeDialog) return
    const startupId = activeDialog.startup.profile.startupId
    // Optimistic dismiss so the card disappears immediately; server hides it
    // on next refetch via the new "passed" filter.
    setDismissed((prev) => new Set(prev).add(startupId))
    closeDialog()
    passMutation.mutate({
      path: { investorId: id, startupId },
      body: { reasons, note: note || null },
    })
  }
  const handleConfirmConnect = (message: string) => {
    if (!activeDialog) return
    const startupId = activeDialog.startup.profile.startupId
    closeDialog()
    connectMutation.mutate({
      path: { investorId: id, startupId },
      body: { message: message || null },
    })
  }

  if (subscriptionQuery.isLoading) {
    return (
      <InvestorLayout activeNav="Deal Flow">
        <FullScreenState loading title="Loading..." />
      </InvestorLayout>
    )
  }

  const isSubscribed = subscriptionQuery.data?.data?.isSubscribed ?? false

  if (!isSubscribed) {
    return (
      <InvestorLayout activeNav="Deal Flow">
        <SubscribeGate
          isSubscribing={subscribeMutation.isPending}
          onSubscribe={() => subscribeMutation.mutate({ path: { investorId: id } })}
        />
      </InvestorLayout>
    )
  }

  if (matchesQuery.isLoading) {
    return (
      <InvestorLayout activeNav="Deal Flow">
        <FullScreenState loading title="Loading matches..." />
      </InvestorLayout>
    )
  }

  return (
    <InvestorLayout activeNav="Deal Flow">
      <div className="mx-auto max-w-6xl py-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Deal Flow</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            AI-matched startups based on your investment thesis
          </p>
        </div>

        {/* Filter + sort */}
        <div className="mt-6 flex items-center justify-between gap-3 border-b border-border">
          <div className="flex gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={cn(
                  'border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                  filter === f.key
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card/30 px-3 py-1.5 text-xs font-medium text-foreground/80 transition-colors hover:text-foreground"
                >
                  Sort: {sortLabel}
                  <ChevronDown className="size-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {SORTS.map((s) => (
                  <DropdownMenuItem
                    key={s.key}
                    onSelect={() => setSort(s.key)}
                    className={cn(sort === s.key && 'text-primary')}
                  >
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={() =>
                navigate({ to: '/app/investor/$id/deal-flow/archive', params: { id } })
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card/30 px-3 py-1.5 text-xs font-medium text-foreground/80 transition-colors hover:text-foreground"
            >
              <Archive className="size-3.5" />
              Archive
            </button>
            <button
              type="button"
              aria-label="Deal Flow preferences"
              onClick={() => navigate({ to: '/app/investor/$id/settings', params: { id } })}
              className="inline-flex items-center justify-center rounded-md border border-border/60 bg-card/30 p-1.5 text-foreground/70 transition-colors hover:text-foreground"
            >
              <Settings className="size-4" />
            </button>
          </div>
        </div>

        {/* Sections */}
        <div className="mt-8 space-y-10">
          <DealFlowSection
            title="Your evaluations"
            subtitle="Startups you've already run custom diligence on"
            count={evaluated.length}
            startups={evaluated}
            renderCard={renderCard}
          />
          <DealFlowSection
            title="In progress"
            subtitle="Diligence is running, check back soon"
            count={inProgress.length}
            startups={inProgress}
            renderCard={renderCard}
          />
          <DealFlowSection
            title="New matches"
            subtitle="Picked from your thesis. Review and pick the ones worth a closer look"
            count={fresh.length}
            startups={fresh}
            renderCard={renderCard}
          />
        </div>

        {visibleStartups.length === 0 && (
          <div className="mt-16 flex flex-col items-center text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-emerald-400/10">
              <Mail className="size-5 text-emerald-400" />
            </div>
            <p className="mt-4 text-sm font-medium text-foreground">
              You're caught up. We're looking for more matches.
            </p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              We're scanning new startups against your thesis around the clock. As soon as we find
              one that fits, we'll drop it here and email you.
            </p>
          </div>
        )}

        {visibleStartups.length > 0 && filteredCount === 0 && (
          <div className="mt-16 flex flex-col items-center text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-emerald-400/10">
              <Mail className="size-5 text-emerald-400" />
            </div>
            <p className="mt-4 text-sm font-medium text-foreground">
              No startups match this filter.
            </p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              Try a different filter to see other matches.
            </p>
          </div>
        )}

        <div className="mt-16 border-t border-border/40 pt-8 text-center">
          <p className="text-xs text-muted-foreground/70">
            Don't want email alerts about new matches?
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <SecondaryButton size="sm" disabled={unsubscribeMutation.isPending} className="mt-2">
                {unsubscribeMutation.isPending ? 'Unsubscribing...' : 'Unsubscribe from Deal Flow'}
              </SecondaryButton>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle>Unsubscribe from Deal Flow?</AlertDialogTitle>
                <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
                  You'll stop receiving email alerts and your matches list will be hidden until you
                  subscribe again. Any pending or completed diligence reports stay in your feed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep deal flow</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => unsubscribeMutation.mutate({ path: { investorId: id } })}
                  className="border-red-500/50 bg-red-500/15 text-red-400 hover:bg-red-500/25 hover:text-red-300"
                >
                  Unsubscribe
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {activeDialog?.kind === 'diligence' && (
        <CustomDiligenceDialog
          investorId={id}
          startupId={activeDialog.startup.profile.startupId}
          startupName={activeDialog.startup.profile.startup.name}
          open
          onOpenChange={(open) => !open && closeDialog()}
        />
      )}
      {activeDialog?.kind === 'connect' && (
        <ConnectDialog
          startupName={activeDialog.startup.profile.startup.name}
          open
          onOpenChange={(open) => !open && closeDialog()}
          onConnect={(message) => handleConfirmConnect(message)}
          isPending={connectMutation.isPending}
        />
      )}
      {activeDialog?.kind === 'pass' && (
        <PassDialog
          startup={activeDialog.startup}
          open
          onOpenChange={(open) => !open && closeDialog()}
          onConfirm={handleConfirmPass}
        />
      )}
    </InvestorLayout>
  )
}
